<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import api from "@/utils/axios";
import Button from "@/components/Base/Button";
import { FormInput, FormLabel } from "@/components/Base/Form";
import QuillEditor from "@/components/Base/QuillEditor/QuillEditor.vue";
import Lucide from "@/components/Base/Lucide";
import { Dialog } from "@/components/Base/Headless";
import { useI18n } from "vue-i18n";
import { useNotification } from "@/composables/useNotification";
import { useAuthStore } from "@/stores/auth";
import { useComposeEmailStore } from "@/stores/composeEmail";
import { formatMailboxSize } from "@/utils/mailbox";

type InternalDoc = {
  id: number;
  title: string;
  original_filename: string;
  size_bytes: number | null;
  mime_type?: string | null;
};

type LocalAttachment = {
  key: string;
  file: File;
};

type DocAttachment = {
  key: string;
  id: number;
  title: string;
  filename: string;
  size: number | null;
};

const { t } = useI18n();
const notify = useNotification();
const authStore = useAuthStore();
const composeStore = useComposeEmailStore();
const { open, to, cc, bcc, subject, body, reply } = storeToRefs(composeStore);

const isSending = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const localAttachments = ref<LocalAttachment[]>([]);
const docAttachments = ref<DocAttachment[]>([]);
const showEditor = ref(false);
const ignoreCloseUntil = ref(0);
const anpaName = ref("ANPA");
const anpaEmail = ref("");
const anpaPhone = ref("");

const docsModalOpen = ref(false);
const docsLoading = ref(false);
const docsQuery = ref("");
const docsItems = ref<InternalDoc[]>([]);

const canUse = computed(() =>
  ["ADMIN", "TECNICO"].includes(String(authStore.user?.role || "").toUpperCase())
);

const dialogOpen = computed(() => open.value && canUse.value);
const isReply = computed(() => !!reply.value);
const title = computed(() =>
  isReply.value ? t("composeEmail.reply_title") : t("composeEmail.title")
);
const selectedDocIds = computed(() => new Set(docAttachments.value.map((d) => d.id)));

const templateFooter = computed(() => {
  const bits = [anpaName.value];
  if (anpaEmail.value) bits.push(anpaEmail.value);
  if (anpaPhone.value) bits.push(anpaPhone.value);
  return bits.join(" · ");
});

const loadAnpaBrand = async () => {
  try {
    const res = await api.get("/anpa");
    const anpa = res.data?.data?.anpa || res.data?.data || {};
    const name = String(anpa.name || "").trim();
    anpaName.value = name || "ANPA";
    anpaEmail.value = String(anpa.email || "").trim();
    anpaPhone.value = String(anpa.phone || "").trim();
  } catch {
    anpaName.value = "ANPA";
    anpaEmail.value = "";
    anpaPhone.value = "";
  }
};

watch(dialogOpen, async (isOpen) => {
  if (isOpen) {
    // Evitar que HeadlessUI peche o modal ao montar Quill (cambio de foco)
    ignoreCloseUntil.value = Date.now() + 400;
    showEditor.value = false;
    void loadAnpaBrand();
    await nextTick();
    window.setTimeout(() => {
      showEditor.value = true;
    }, 80);
  } else {
    showEditor.value = false;
    localAttachments.value = [];
    docAttachments.value = [];
    docsModalOpen.value = false;
    docsQuery.value = "";
    docsItems.value = [];
  }
});

const onDialogClose = () => {
  if (Date.now() < ignoreCloseUntil.value) {
    return;
  }
  composeStore.closeCompose();
};

const formatSize = (bytes: number | null | undefined) => formatMailboxSize(bytes);

const stripHtml = (html: string) =>
  html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const onPickFiles = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files || []);
  for (const file of files) {
    if (file.size > 10 * 1024 * 1024) {
      notify.error(t("common.error"), t("composeEmail.file_too_large", { name: file.name }));
      continue;
    }
    localAttachments.value.push({
      key: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
      file,
    });
  }
  input.value = "";
};

const removeLocal = (key: string) => {
  localAttachments.value = localAttachments.value.filter((a) => a.key !== key);
};

const removeDoc = (key: string) => {
  docAttachments.value = docAttachments.value.filter((a) => a.key !== key);
};

const openDocsPicker = async () => {
  docsModalOpen.value = true;
  await searchDocs();
};

const searchDocs = async () => {
  docsLoading.value = true;
  try {
    const res = await api.get("/documents", {
      params: {
        scope: "all",
        q: docsQuery.value.trim() || undefined,
      },
    });
    docsItems.value = res.data?.data?.items || [];
  } catch (e: any) {
    docsItems.value = [];
    notify.error(t("common.error"), e.response?.data?.message || t("composeEmail.docs_error"));
  } finally {
    docsLoading.value = false;
  }
};

const toggleDoc = (doc: InternalDoc) => {
  if (selectedDocIds.value.has(doc.id)) {
    docAttachments.value = docAttachments.value.filter((d) => d.id !== doc.id);
    return;
  }
  docAttachments.value.push({
    key: `doc-${doc.id}`,
    id: doc.id,
    title: doc.title,
    filename: doc.original_filename || doc.title,
    size: doc.size_bytes,
  });
};

const send = async () => {
  if (!canUse.value) return;
  const toValue = to.value.trim();
  if (!toValue) {
    notify.error(t("common.error"), t("composeEmail.to_required"));
    return;
  }
  if (!subject.value.trim()) {
    notify.error(t("common.error"), t("composeEmail.subject_required"));
    return;
  }
  if (!stripHtml(body.value)) {
    notify.error(t("common.error"), t("composeEmail.body_required"));
    return;
  }

  isSending.value = true;
  try {
    const form = new FormData();
    form.append("to", toValue);
    form.append("cc", cc.value.trim());
    form.append("bcc", bcc.value.trim());
    form.append("subject", subject.value.trim());
    form.append("body", body.value);
    if (reply.value) {
      form.append("reply_folder", reply.value.folder);
      form.append("reply_uid", String(reply.value.uid));
    }
    for (const item of localAttachments.value) {
      form.append("files[]", item.file, item.file.name);
    }
    for (const doc of docAttachments.value) {
      form.append("document_ids[]", String(doc.id));
    }

    const res = await api.post("/admin/mailbox/send", form);
    notify.success(t("common.success"), res.data?.message || t("composeEmail.sent"));
    composeStore.closeCompose();
  } catch (e: any) {
    notify.error(
      t("common.error"),
      e.response?.data?.message || t("composeEmail.send_error")
    );
  } finally {
    isSending.value = false;
  }
};
</script>

<template>
  <Dialog size="xl" :open="dialogOpen" static-backdrop @close="onDialogClose">
    <Dialog.Panel class="max-w-4xl w-full">
      <Dialog.Title class="relative pr-12">
        <div class="flex items-center gap-2 mr-auto">
          <div class="rounded-lg bg-teal-700/10 p-2">
            <Lucide :icon="isReply ? 'Reply' : 'PenSquare'" class="w-5 h-5 text-teal-700" />
          </div>
          <div>
            <h2 class="text-base font-medium">{{ title }}</h2>
            <p class="text-xs text-slate-500 mt-0.5">{{ t("composeEmail.template_hint") }}</p>
          </div>
        </div>
        <button
          type="button"
          class="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          @click="composeStore.closeCompose()"
        >
          <Lucide icon="X" class="w-5 h-5" />
        </button>
      </Dialog.Title>

      <div class="p-5 space-y-3 max-h-[75vh] overflow-y-auto">
        <div class="grid grid-cols-12 gap-3">
          <div class="col-span-12 sm:col-span-2 flex items-center">
            <FormLabel class="mb-0 text-slate-500">{{ t("composeEmail.to") }}</FormLabel>
          </div>
          <div class="col-span-12 sm:col-span-10">
            <FormInput v-model="to" type="text" :placeholder="t('composeEmail.to_placeholder')" />
          </div>

          <div class="col-span-12 sm:col-span-2 flex items-center">
            <FormLabel class="mb-0 text-slate-500">{{ t("composeEmail.cc") }}</FormLabel>
          </div>
          <div class="col-span-12 sm:col-span-10">
            <FormInput v-model="cc" type="text" :placeholder="t('composeEmail.cc_placeholder')" />
          </div>

          <div class="col-span-12 sm:col-span-2 flex items-center">
            <FormLabel class="mb-0 text-slate-500">{{ t("composeEmail.bcc") }}</FormLabel>
          </div>
          <div class="col-span-12 sm:col-span-10">
            <FormInput v-model="bcc" type="text" :placeholder="t('composeEmail.bcc_placeholder')" />
          </div>

          <div class="col-span-12 sm:col-span-2 flex items-center">
            <FormLabel class="mb-0 text-slate-500">{{ t("composeEmail.subject") }}</FormLabel>
          </div>
          <div class="col-span-12 sm:col-span-10">
            <FormInput v-model="subject" />
          </div>
        </div>

        <div>
          <FormLabel class="text-slate-500 mb-2">{{ t("composeEmail.body") }}</FormLabel>
          <div class="rounded-xl border border-slate-200/80 bg-[#eef2f4] p-3 sm:p-4">
            <div class="mx-auto max-w-xl overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-slate-200/60">
              <div class="bg-[#0f766e] px-5 py-4 text-white">
                <div
                  class="text-[11px] font-medium uppercase tracking-[0.14em] text-[#d1fae5]"
                >
                  {{ t("composeEmail.template_eyebrow") }}
                </div>
                <div class="mt-1.5 text-[22px] font-bold leading-snug">
                  {{ anpaName }}
                </div>
              </div>

              <div class="compose-template-body px-2 py-3 sm:px-3">
                <QuillEditor
                  v-if="showEditor"
                  v-model="body"
                  variant="email"
                  :placeholder="t('composeEmail.body_placeholder')"
                />
                <div
                  v-else
                  class="flex min-h-[220px] items-center justify-center text-sm text-slate-400"
                >
                  {{ t("common.loading") }}...
                </div>
              </div>

              <div class="px-5 pb-5 pt-1">
                <div class="border-t border-slate-200 pt-3 text-xs leading-relaxed text-slate-500">
                  {{ templateFooter }}
                  <br />
                  <span class="text-slate-400">{{ t("composeEmail.template_sent_via") }}</span>
                </div>
              </div>
            </div>
            <p class="mt-2 text-center text-[11px] text-slate-500">
              {{ t("composeEmail.preview_help") }}
            </p>
          </div>
        </div>

        <div class="rounded-xl border border-slate-200/80 bg-slate-50/70 p-3">
          <div class="flex flex-wrap items-center gap-2 mb-2">
            <div class="text-sm font-medium text-slate-700 mr-auto">
              {{ t("composeEmail.attachments") }}
            </div>
            <input ref="fileInput" type="file" class="hidden" multiple @change="onPickFiles" />
            <Button size="sm" variant="outline-secondary" @click="fileInput?.click()">
              <Lucide icon="Paperclip" class="w-3.5 h-3.5 mr-1" />
              {{ t("composeEmail.attach_files") }}
            </Button>
            <Button size="sm" variant="outline-primary" @click="openDocsPicker">
              <Lucide icon="FolderOpen" class="w-3.5 h-3.5 mr-1" />
              {{ t("composeEmail.attach_docs") }}
            </Button>
          </div>

          <ul v-if="localAttachments.length || docAttachments.length" class="space-y-1.5">
            <li
              v-for="item in localAttachments"
              :key="item.key"
              class="flex items-center gap-2 text-sm bg-white rounded-md px-2.5 py-1.5 border border-slate-200/80"
            >
              <Lucide icon="File" class="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span class="truncate flex-1">{{ item.file.name }}</span>
              <span class="text-xs text-slate-400">{{ formatSize(item.file.size) }}</span>
              <button type="button" class="text-danger" @click="removeLocal(item.key)">
                <Lucide icon="X" class="w-3.5 h-3.5" />
              </button>
            </li>
            <li
              v-for="item in docAttachments"
              :key="item.key"
              class="flex items-center gap-2 text-sm bg-white rounded-md px-2.5 py-1.5 border border-teal-700/15"
            >
              <Lucide icon="Library" class="w-3.5 h-3.5 text-teal-700 shrink-0" />
              <span class="truncate flex-1">
                {{ item.title }}
                <span class="text-slate-400">· {{ item.filename }}</span>
              </span>
              <span class="text-xs text-slate-400">{{ formatSize(item.size) }}</span>
              <button type="button" class="text-danger" @click="removeDoc(item.key)">
                <Lucide icon="X" class="w-3.5 h-3.5" />
              </button>
            </li>
          </ul>
          <p v-else class="text-xs text-slate-500">{{ t("composeEmail.attachments_empty") }}</p>
        </div>
      </div>

      <Dialog.Footer>
        <Button
          variant="outline-secondary"
          class="mr-1"
          :disabled="isSending"
          @click="composeStore.closeCompose()"
        >
          {{ t("common.cancel") }}
        </Button>
        <Button variant="primary" :disabled="isSending" @click="send">
          <Lucide icon="Send" class="w-4 h-4 mr-2" />
          {{ isSending ? t("common.loading") : t("composeEmail.send") }}
        </Button>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>

  <Dialog :open="docsModalOpen" @close="docsModalOpen = false">
    <Dialog.Panel class="max-w-2xl w-full">
      <Dialog.Title>
        <h2 class="mr-auto text-base font-medium">{{ t("composeEmail.attach_docs") }}</h2>
      </Dialog.Title>
      <div class="p-5">
        <div class="flex gap-2">
          <FormInput
            v-model="docsQuery"
            class="flex-1"
            :placeholder="t('composeEmail.docs_search')"
            @keyup.enter="searchDocs"
          />
          <Button variant="primary" :disabled="docsLoading" @click="searchDocs">
            <Lucide icon="Search" class="w-4 h-4" />
          </Button>
        </div>
        <div v-if="docsLoading" class="py-8 text-center text-slate-500">
          {{ t("common.loading") }}...
        </div>
        <ul v-else class="mt-3 max-h-[50vh] overflow-y-auto divide-y divide-slate-100">
          <li
            v-for="doc in docsItems"
            :key="doc.id"
            class="flex items-center gap-3 py-2.5 cursor-pointer hover:bg-slate-50 px-2 rounded"
            @click="toggleDoc(doc)"
          >
            <div
              class="w-5 h-5 rounded border flex items-center justify-center shrink-0"
              :class="
                selectedDocIds.has(doc.id)
                  ? 'bg-teal-700 border-teal-700 text-white'
                  : 'border-slate-300'
              "
            >
              <Lucide v-if="selectedDocIds.has(doc.id)" icon="Check" class="w-3.5 h-3.5" />
            </div>
            <div class="min-w-0 flex-1">
              <div class="text-sm font-medium truncate">{{ doc.title }}</div>
              <div class="text-xs text-slate-500 truncate">
                {{ doc.original_filename }} · {{ formatSize(doc.size_bytes) }}
              </div>
            </div>
          </li>
          <li v-if="!docsItems.length" class="py-8 text-center text-slate-500 text-sm">
            {{ t("composeEmail.docs_empty") }}
          </li>
        </ul>
      </div>
      <Dialog.Footer>
        <Button variant="primary" @click="docsModalOpen = false">
          {{ t("common.close") }}
        </Button>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>

<style scoped>
.compose-template-body :deep(.quill-editor-wrapper.is-email .ql-toolbar.ql-snow) {
  border: none;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 0;
  background: #f8fafc;
}
.compose-template-body :deep(.quill-editor-wrapper.is-email .ql-container.ql-snow) {
  border: none;
  border-radius: 0;
}
.compose-template-body :deep(.quill-editor-wrapper.is-email .ql-editor) {
  min-height: 200px;
  max-height: 320px;
  font-size: 16px;
  line-height: 1.65;
  color: #334155;
  font-family: Arial, Helvetica, sans-serif;
}
</style>
